import type { MuxPlayerProps } from "@mux/mux-player-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Icon } from "@wayflyer/flyui";
import { IconPlay16Line } from "@wayflyer/flyui-icons/16/line";
import React, { type PropsWithChildren } from "react";
import { styled } from "styled-components";

import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "./dialog";
import { MuxPlayer } from "./mux-player-react";

export function DialogVideo({
  children,
  playbackId,
  title,
}: { children: React.ReactNode } & Required<Pick<MuxPlayerProps, "playbackId" | "title">>) {
  return (
    <Dialog>
      {/* @ts-expect-error strange child error */}
      <DialogTrigger role="button" asChild>
        {children}
      </DialogTrigger>
      <DialogContent>
        <VisuallyHidden.Root>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden.Root>
        <VideoContainer>
          <MuxPlayer autoPlay style={{ position: "absolute", inset: "0px" }} playbackId={playbackId} title={title} />
        </VideoContainer>
      </DialogContent>
    </Dialog>
  );
}

const IconContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  background-color: var(--palette-bg-neutral-inverse);
  padding: var(--sizes-spacing-2);
`;

export function CardImageContainer({ children }: PropsWithChildren) {
  return (
    <div style={{ position: "relative" }}>
      <IconContainer>
        <Icon color="onDark">
          <IconPlay16Line style={{ flexShrink: 0 }} aria-label="play" />
        </Icon>
      </IconContainer>
      {children}
    </div>
  );
}

// prevents layout shift https://www.mux.com/blog/mux-player-lazy-loading-with-blurhash
function VideoContainer({ children }: PropsWithChildren) {
  return (
    <div
      style={{
        position: "relative",
        aspectRatio: "16/9",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "0px",
        }}
      />
      {children}
    </div>
  );
}

export const AccordionGridItem = styled.div`
  display: grid;
  grid-column: span 12 / span 12;
  row-gap: 8px;
  @media (min-width: 1024px) {
    grid-column: span 7 / span 7;
  }
`;

export const Card = styled.div`
  display: grid;
  gap: 10px;
  grid-column: span 12 / span 12;
  @media (min-width: 1024px) {
    grid-column: span 4 / span 4;
  }
`;

export const CardImg = styled.img.attrs({
  height: 360,
  sizes: "(max-width: 768) 100vw, 40vw",
  decoding: "async",
  loading: "lazy",
})`
  border-radius: 4px;
  object-fit: cover;
  width: 100%;
  max-height: 240px;

  @media (min-width: 768px) {
    max-height: 360px;
  }
  @media (min-width: 1024px) {
    aspect-ratio: 1/1;
    min-height: 360px;
  }
`;

export const GridLayout = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
`;

export const HalfColGridItem = styled.div`
  display: grid;
  grid-column: span 12 / span 12;
  @media (min-width: 1024px) {
    grid-column: span 6 / span 6;
  }
`;

export const FullColGridItem = styled.div`
  display: grid;
  grid-column: span 12 / span 12;
`;

export const HeroList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  margin-top: 24px;
`;

export const StyledLi = styled.li`
  display: flex;
  gap: var(--sizes-spacing-8);
  margin-top: 12px;
  &:first-child {
    margin-top: 0;
  }
`;

export function getImageSrcSet(src: string) {
  return `${src}?w=640&q=75 640w, ${src}?w=750&q=75 750w, ${src}?w=828&q=75 828w, ${src}?w=1080&q=75 1080w, ${src}?w=1200&q=75 1200w, ${src}?w=1920&q=75 1920w, ${src}?w=2048&q=75 2048w, ${src}?w=3840&q=75 3840w`;
}
